import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Form, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
    getOrganizationTaxConfigurationAPI,
    postOrganizationTaxScheduleAPI,
    putOrganizationTaxScheduleAPI
} from '../store/user/restCallUser';
import {
    EffectiveTaxRate,
    OrganizationTaxConfigurationResponse,
    OrganizationTaxSchedule
} from '../store/user/types';

interface Props { organizationId: string; organizationName?: string; }

const dataOf = (response: any) => {
    const value = response?.Response?.data || response?.Response;
    try { return typeof value === 'string' ? JSON.parse(value) : value; } catch { return value; }
};

export default function OrganizationTaxConfigurationManagement({ organizationId, organizationName }: Props) {
    const [form] = Form.useForm();
    const [configuration, setConfiguration] = useState<OrganizationTaxConfigurationResponse | null>(null);
    const [editing, setEditing] = useState<OrganizationTaxSchedule | null>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getOrganizationTaxConfigurationAPI(organizationId);
            if (response.StatusCode < 200 || response.StatusCode >= 300) throw new Error(response.Error?.message || 'No se pudo cargar la configuración de IVA.');
            setConfiguration(dataOf(response) as OrganizationTaxConfigurationResponse);
        } catch (error: any) {
            message.error(error?.message || 'No se pudo cargar la configuración de IVA.');
        } finally { setLoading(false); }
    }, [organizationId]);

    useEffect(() => { void load(); }, [load]);

    const effectiveColumns = useMemo<ColumnsType<EffectiveTaxRate>>(() => [
        { title: 'Código', dataIndex: 'code' },
        { title: 'Nombre', dataIndex: 'name' },
        { title: 'Tratamiento', dataIndex: 'taxTreatment' },
        { title: 'Tarifa', render: (_, row) => row.rate == null ? 'No aplica' : `${Number(row.rate).toFixed(2)} %` },
        { title: 'Código SRI', render: (_, row) => `${row.sriTaxCode || '—'} / ${row.sriPercentageCode || '—'}` },
        { title: 'Origen', render: (_, row) => <Tag color={row.isOrganizationDefault ? 'blue' : 'default'}>{row.isOrganizationDefault ? 'Predeterminado organización' : 'Catálogo global'}</Tag> }
    ], []);

    const scheduleColumns = useMemo<ColumnsType<OrganizationTaxSchedule>>(() => [
        { title: 'Configuración', render: (_, row) => `${row.taxRateName} (${row.taxRateCode})` },
        { title: 'Tarifa', render: (_, row) => row.rate == null ? 'No aplica' : `${Number(row.rate).toFixed(2)} %` },
        { title: 'Desde', dataIndex: 'effectiveFrom' },
        { title: 'Hasta', render: (_, row) => row.effectiveTo || 'Sin fecha final' },
        { title: 'Estado', render: (_, row) => <Tag color={row.status === 'ACTIVO' ? 'green' : 'default'}>{row.status}</Tag> },
        { title: 'Acciones', render: (_, row) => <Button size="small" onClick={() => {
            setEditing(row);
            form.setFieldsValue({
                taxRateDefinitionId: row.taxRateDefinitionId,
                effectiveFrom: dayjs(row.effectiveFrom),
                effectiveTo: row.effectiveTo ? dayjs(row.effectiveTo) : null,
                status: row.status
            });
            setOpen(true);
        }}>Editar</Button> }
    ], [form]);

    const save = async () => {
        const values = await form.validateFields();
        const payload = {
            taxRateDefinitionId: values.taxRateDefinitionId,
            effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD'),
            effectiveTo: values.effectiveTo ? values.effectiveTo.format('YYYY-MM-DD') : null,
            status: values.status
        };
        setLoading(true);
        try {
            const response = editing
                ? await putOrganizationTaxScheduleAPI(organizationId, editing.id, payload)
                : await postOrganizationTaxScheduleAPI(organizationId, payload);
            if (response.StatusCode < 200 || response.StatusCode >= 300) throw new Error(response.Error?.message || 'No se pudo guardar la configuración.');
            setOpen(false); setEditing(null); form.resetFields(); await load();
            message.success('Configuración de IVA guardada.');
        } catch (error: any) { message.error(error?.message || 'No se pudo guardar la configuración.'); }
        finally { setLoading(false); }
    };

    return <>
        <Alert
            type="info"
            showIcon
            message={`IVA de ${organizationName || 'la organización'}`}
            description="Blue mantiene las tarifas y códigos legales del SRI. Aquí la organización define cuál será su configuración predeterminada por vigencia; al facturar todavía podrá elegir otra tarifa global válida para cada ítem."
            style={{ marginBottom: 16 }}
        />
        <Typography.Title level={5}>Tarifas disponibles al {configuration?.evaluationDate || 'día de hoy'}</Typography.Title>
        <Table rowKey="id" size="small" columns={effectiveColumns} dataSource={configuration?.effectiveRates || []} loading={loading} pagination={false} />
        <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 24, marginBottom: 12 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Vigencias de la organización</Typography.Title>
            <Button type="primary" onClick={() => {
                setEditing(null);
                form.setFieldsValue({ effectiveFrom: dayjs(), effectiveTo: null, status: 'ACTIVO' });
                setOpen(true);
            }}>Nueva vigencia</Button>
        </Space>
        <Table rowKey="id" size="small" columns={scheduleColumns} dataSource={configuration?.schedules || []} loading={loading} pagination={false} />
        <Modal open={open} title={editing ? 'Editar vigencia de IVA' : 'Nueva vigencia de IVA'} onCancel={() => setOpen(false)} onOk={() => void save()} confirmLoading={loading}>
            <Form form={form} layout="vertical">
                <Form.Item name="taxRateDefinitionId" label="Configuración predeterminada" rules={[{ required: true }]}>
                    <Select options={(configuration?.definitions || []).filter(x => x.status === 'ACTIVO').map(x => ({ value: x.id, label: `${x.name} — ${x.rate == null ? 'No aplica' : `${Number(x.rate).toFixed(2)} %`}` }))} />
                </Form.Item>
                <Space size={12} style={{ width: '100%' }}>
                    <Form.Item name="effectiveFrom" label="Desde" rules={[{ required: true }]}><DatePicker format="YYYY-MM-DD" /></Form.Item>
                    <Form.Item name="effectiveTo" label="Hasta"><DatePicker format="YYYY-MM-DD" /></Form.Item>
                </Space>
                <Form.Item name="status" label="Estado" rules={[{ required: true }]}><Select options={[{ value: 'ACTIVO' }, { value: 'INACTIVO' }]} /></Form.Item>
            </Form>
        </Modal>
    </>;
}
